import React, { useState, useEffect } from 'react';
import './ComposeBox.css';
import { Copy01Icon, SentIcon, Tick01Icon, Mail01Icon, Message01Icon } from 'hugeicons-react';
import { AIDisplayRenderer } from '../../handler/TextRenderer/LLMTextRenderer';

export interface ComposeOption {
  title?: string;
  subject?: string;
  content: string;
}

/**
 * - 'auto': derive email vs message from whether `to` is present.
 * - 'email': always treat as an email (legacy <mail> blocks, which may lack a recipient).
 * - 'pending': still streaming and not yet known — show a neutral "Draft" header.
 */
export type ComposeBoxMode = 'auto' | 'email' | 'pending';

export interface ComposeBoxProps {
  to?: string;
  options: ComposeOption[];
  mode?: ComposeBoxMode;
  isGenerating?: boolean;
}

export const ComposeBox: React.FC<ComposeBoxProps> = ({
  to,
  options,
  mode = 'auto',
  isGenerating = false,
}) => {
  const safeOptions = options && options.length > 0 ? options : [{ content: '' }];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isCopied, setIsCopied] = useState(false);

  // While streaming, follow the latest option so the user sees the most recent variant.
  useEffect(() => {
    if (isGenerating) {
      setSelectedIndex(safeOptions.length - 1);
    }
  }, [isGenerating, safeOptions.length]);

  const activeIndex = Math.min(selectedIndex, safeOptions.length - 1);
  const active = safeOptions[activeIndex];
  const trimmedContent = (active.content || '').trim();
  const trimmedSubject = (active.subject || '').trim();
  const trimmedTo = (to || '').trim();
  const isEmailMode = mode === 'email' || (mode === 'auto' && trimmedTo.length > 0);
  const isModePending = mode === 'pending';

  const showOptionPills = safeOptions.length > 1;

  const headerLabel = isModePending ? 'Draft' : isEmailMode ? 'Email' : 'Message';
  const HeaderIcon = isEmailMode ? Mail01Icon : Message01Icon;

  const mailtoLink = isEmailMode
    ? `mailto:${encodeURIComponent(trimmedTo)}?subject=${encodeURIComponent(trimmedSubject)}&body=${encodeURIComponent(trimmedContent)}`
    : '';

  const handleOpenEmail = () => {
    if (!isEmailMode) return;
    window.location.href = mailtoLink;
  };

  const handleCopyContent = async () => {
    if (!trimmedContent) return;

    try {
      await navigator.clipboard.writeText(trimmedContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy compose content:', error);
    }
  };

  return (
    <div className={`compose-box ${isGenerating ? 'compose-box--generating' : ''}`}>
      <div className="compose-box__header">
        <div className="compose-box__header-left">
          <HeaderIcon size={16} className="compose-box__icon" />
          <span className="compose-box__title">{headerLabel}</span>
          {showOptionPills && (
            <div className="compose-box__options" role="tablist" aria-label="Compose variants">
              {safeOptions.map((opt, idx) => {
                const label = (opt.title || '').trim() || `Option ${idx + 1}`;
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={`${idx}-${label}`}
                    role="tab"
                    aria-selected={isActive}
                    className={`compose-box__option-pill${isActive ? ' compose-box__option-pill--active' : ''}`}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {!isGenerating ? (
          <div className="compose-box__actions">
            <button
              type="button"
              className="compose-box__action-btn"
              onClick={handleCopyContent}
              title="Copy content"
              aria-label="Copy content"
              disabled={!trimmedContent}
            >
              {isCopied ? <Tick01Icon size={20} /> : <Copy01Icon size={20} />}
            </button>
            {isEmailMode && (
              <button
                type="button"
                className="compose-box__action-btn"
                onClick={handleOpenEmail}
                title="Open in email client"
                aria-label="Open in email client"
              >
                <SentIcon size={20} />
              </button>
            )}
          </div>
        ) : (
          <div className="compose-box__generating-badge">
            <div className="compose-box__spinner" />
            <span>Composing</span>
          </div>
        )}
      </div>

      {((isEmailMode && trimmedTo) || trimmedSubject) && (
        <div className="compose-box__fields">
          {isEmailMode && trimmedTo && (
            <div className="compose-box__field">
              <span className="compose-box__label">To</span>
              <span className="compose-box__value">{trimmedTo}</span>
            </div>
          )}

          {trimmedSubject && (
            <div className="compose-box__subject">{trimmedSubject}</div>
          )}
        </div>
      )}
 

      {(trimmedContent || isGenerating) && (
        <>
          <div className="compose-box__body-wrapper">
            <div className="compose-box__body">
              <AIDisplayRenderer content={trimmedContent} isGenerating={isGenerating} />
            </div>
            <div className="compose-box__body-fade" />
          </div>
        </>
      )}
    </div>
  );
};
